import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Phone, Sparkles, X, Send, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function FloatingChatAdmin() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  const phoneNumber = "081261089400";
  const displayPhone = "0812-6108-9400";
  const defaultMessage = "Halo Admin Azman204, saya ingin konsultasi dan tanya seputar layanan atau format dokumen konstruksi. Terima kasih.";

  const openWhatsApp = (customText?: string) => {
    const textToSend = customText || defaultMessage;
    const url = `https://wa.me/6281261089400?text=${encodeURIComponent(textToSend)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyPhone = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(phoneNumber);
    setHasCopied(true);
    toast.success("Nomor WhatsApp disalin: 081261089400");
    setTimeout(() => setHasCopied(false), 2500);
  };

  const quickMessages = [
    {
      title: "Format Dokumen Konstruksi",
      desc: "SMKK, RKK, RAB Excel, Tender PUPR",
      text: "Halo Admin Azman204, saya ingin menanyakan paket format dokumen konstruksi (SMKK, RKK, RAB, Tender)."
    },
    {
      title: "Konsultasi Layanan & Perizinan",
      desc: "Pendampingan proyek, tender & pajak",
      text: "Halo Admin Azman204, saya butuh informasi konsultasi layanan perizinan dan pendampingan proyek."
    },
    {
      title: "Bantuan Umum / Pertanyaan Lain",
      desc: "Respon cepat tim admin Azman204",
      text: "Halo Admin Azman204, saya ingin bertanya mengenai layanan di website Azman204."
    }
  ];

  return (
    <div className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-40 select-none">
      <AnimatePresence>
        {/* Quick Chat Popup Card */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 16 }}
            transition={{ type: "spring", damping: 25, stiffness: 350 }}
            className="mb-3 w-[calc(100vw-2rem)] sm:w-84 max-w-sm bg-card border border-border/80 rounded-3xl shadow-2xl overflow-hidden backdrop-blur-xl"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-teal-700 text-white p-4 sm:p-5 relative">
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-3.5 right-3.5 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                aria-label="Tutup menu chat"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-md">
                    <MessageCircle className="h-6 w-6 text-white" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400 border-2 border-emerald-700"></span>
                  </span>
                </div>
                <div>
                  <h4 className="font-black text-white text-base leading-tight">Admin Azman204</h4>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse"></span>
                    <p className="text-xs text-emerald-100 font-medium">Online • Siap Membantu</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Body */}
            <div className="p-4 sm:p-5 space-y-4 bg-card">
              <p className="text-xs text-foreground/70 leading-relaxed">
                Silakan pilih topik konsultasi atau langsung hubungi admin melalui WhatsApp untuk respon tercepat:
              </p>

              {/* Quick message options */}
              <div className="space-y-2">
                {quickMessages.map((msg, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      openWhatsApp(msg.text);
                      setIsOpen(false);
                    }}
                    className="w-full text-left p-3 rounded-2xl border border-border bg-muted/40 hover:bg-muted hover:border-emerald-500/40 transition-all group flex items-center justify-between"
                  >
                    <div className="pr-2">
                      <p className="text-xs font-bold text-foreground group-hover:text-emerald-600 transition-colors">
                        {msg.title}
                      </p>
                      <p className="text-[10px] text-foreground/50 line-clamp-1">{msg.desc}</p>
                    </div>
                    <Send className="h-3.5 w-3.5 text-foreground/30 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>
                ))}
              </div>

              {/* Direct WhatsApp Call-To-Action Button */}
              <button
                onClick={() => {
                  openWhatsApp();
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold text-xs shadow-lg shadow-emerald-600/25 transition-all active:scale-95"
              >
                <MessageCircle className="h-4 w-4" />
                <span>Buka Chat WhatsApp Langsung</span>
                <ExternalLink className="h-3.5 w-3.5 opacity-70" />
              </button>

              {/* Phone & Copy actions */}
              <div className="pt-2 border-t border-border flex items-center justify-between text-[11px] text-foreground/60">
                <span className="font-semibold">{displayPhone}</span>
                <button
                  onClick={handleCopyPhone}
                  className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-bold transition-colors"
                >
                  {hasCopied ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                      <span>Tersalin</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      <span>Salin Nomor</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Floating Trigger Button */}
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="relative flex items-center"
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="group flex items-center gap-2.5 px-4 py-3 sm:px-5 sm:py-3.5 rounded-full bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-700 hover:from-emerald-500 hover:to-green-500 text-white shadow-xl shadow-emerald-900/30 border border-emerald-400/30 backdrop-blur-md transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/20"
          aria-label="Chat Admin WhatsApp"
          title="Hubungi Admin Azman204 via WhatsApp"
        >
          {/* Online badge dot */}
          <span className="relative flex h-3 w-3 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-200 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-300"></span>
          </span>

          <MessageCircle className="h-5 w-5 text-white transition-transform duration-300 group-hover:rotate-12" />

          <span className="text-xs sm:text-sm font-extrabold tracking-wide whitespace-nowrap shadow-sm">
            Chat Admin
          </span>

          {isOpen && <X className="h-4 w-4 ml-1 text-white/80" />}
        </button>
      </motion.div>
    </div>
  );
}
